import BackButton from "components/BackButton";
import ConnectionStatus from "components/ConnectionStatus";
import BasicButton from "components/General/BasicButton";
import GameIdButton from "components/Host/GameIdButton";
import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useWebSocket } from "WebSocketContext";
import { useVowGameContext } from "VowGameContext";
import { createPlayers, type PlayerKey } from "./vowGameLogic";

const roleNameMap: Record<PlayerKey, string> = (() => {
  const base = createPlayers();
  return {
    herder: base.herder.name,
    reed_harvester: base.reed_harvester.name,
    hunter: base.hunter.name,
    protector: base.protector.name,
    bittern: base.bittern.name,
  };
})();

const generateRoomCode = (seed: number, length = 4) => {
  const randomFactor = Math.floor(Math.random() * 36 ** length);
  const combined = seed + randomFactor;
  const base36Hash = combined.toString(36).toUpperCase();
  return base36Hash.padStart(length, "0").substring(0, length);
};

const VowHost: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected, playerId } = useWebSocket();
  const {
    gameId,
    players,
    createOrJoinHostRoom,
  } = useVowGameContext();

  useEffect(() => {
    if (!isConnected || playerId <= 0) return;
    if (gameId) return;
    const code = generateRoomCode(playerId);
    createOrJoinHostRoom(code);
  }, [createOrJoinHostRoom, gameId, isConnected, playerId]);

  const playerList = useMemo(() => [...players].sort((a, b) => a.id - b.id), [players]);

  return (
    <div className="flex min-h-screen flex-col items-center gap-6 bg-[#f3eddb] px-4 py-10">
      <ConnectionStatus />
      <div className="w-full max-w-3xl border-2 border-black bg-[#BCB7A8] p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-slate-900">Host a VOW Session</h1>
            <p className="text-sm text-slate-800">
              Share the invite code below so that players can join your game.
            </p>
          </div>
          <BackButton useAbsolute={false} />
        </div>

        <div className="mt-5 flex flex-col items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-800">
            Invite Code
          </span>
          <GameIdButton gameId={gameId} />
        </div>

        <div className="mt-6 space-y-2">
          <h2 className="text-lg font-semibold text-slate-900">Players</h2>
          <p className="text-sm text-slate-800">
            Roles can be selected once players enter the game screen. Launch the session from the
            game board when everyone is ready.
          </p>
          <ul className="mt-3 border-2 border-black bg-[#faf3e2]">
            {playerList.length === 0 && (
              <li className="px-4 py-3 text-sm text-slate-700">
                Waiting for players to join…
              </li>
            )}
            {playerList.map((player, index) => (
              <li
                key={player.id}
                className={`flex items-center justify-between px-4 py-3 text-slate-900 ${
                  index !== playerList.length - 1 ? "border-b border-black/30" : ""
                }`}
              >
                <div>
                  <p className="font-medium">
                    {player.playerName || `Player ${player.id}`}
                  </p>
                  <p className="text-[11px] uppercase tracking-wide text-slate-700">
                    ID: {player.id}
                  </p>
                </div>
                <p className="text-sm font-semibold">
                  {player.role ? roleNameMap[player.role] : "Role not selected"}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 flex justify-end">
          <BasicButton
            className={`px-6 py-3 ${(!isConnected || playerId <= 0) ? "opacity-50 cursor-not-allowed" : ""}`}
            text="Enter Game"
            onClick={() => {
              if (!isConnected || playerId <= 0) return;
              navigate("/vow");
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default VowHost;
